import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import FormAccordion from "../../../components/Form/FormAccordion";
import {
	FileGroup,
	InputField,
	SelectField,
	SelectFieldStatus,
	TextAreaField,
} from "../../../components/Form/FormComponents";
import { getFormattedDate } from "../../../utils/DateUtils";
import { getApprovalStatus, getSubmittedQuickLease, getUserName } from "../utils";
import { getLeasor, getRequestValue } from "../../../utils/Utils";
import { Guarantor_1, Guarantor_2, LeaseeInformation } from "../partials/FormFields";
import { StyledForm } from "../../../components/Form/FormComponents.styled";
import RequestStatusForm from "../../../components/common/RequestStatusForm";
import AdminApprovalStatus from "./AdminApprovalStatus";
import { getFormattedAmount } from "../../../utils/AmountUtils";
import FundLeaseRequest from "./FundLeaseRequest";
import { useDispatch, useSelector } from "react-redux";
import { saveAdminQuickRequests, toggleFundLeaseRequest } from "../LeaseSlice";
import { Backdrop } from "@mui/material";
import SponsoredRequestStatus from "../../../components/common/SponsoredRequestStatus";

const RequestLeaseForm = ({ leaseData, setLeaseData, updateLeaseObject }) => {
	const dispatch = useDispatch();
	const { toggleFundLeaseRequestModal } = useSelector((state) => state.lease);
	const [formData, setFormData] = useState(leaseData);
	const [action] = useState("view");
	const [fundRequestTab, setFundRequestTab] = useState(1);

	const isEditable = action === "edit";
	const handleInputChange = (e) => {
		setFormData({ ...formData, [e.target.name]: e.target.value });
	};

	const handleSubmit = (e) => {
		e.preventDefault();
		// console.log(formData);
		setLeaseData(formData);
		updateLeaseObject(formData);
	};

	const onBeginFundRequest = () => {
		dispatch(toggleFundLeaseRequest(true));
		setFundRequestTab(1);
	};

	const adminComments = [
		{
			admin: "Basic Admin (R&C Dept)",
			status: formData?.basicAdminApproval?.status,
			comment: formData?.basicAdminApproval?.comment,
		},
		{
			admin: "1st Line Admin (R&C Dept",
			status: formData?.firstLineApproval?.status,
			comment: formData?.firstLineApproval?.comment,
		},
		{
			admin: "2nd Line Admin (R&C Dept)",
			status: formData?.secondLineApproval?.status,
			comment: formData?.secondLineApproval?.comment,
		},
		{
			admin: "Final Line Admin (R&C Dept)",
			status: formData?.finalApproval?.status,
			comment: formData?.finalApproval?.comment,
		},
		{
			admin: "Admin (Finance Dept)",
			status: formData?.financeApproval?.status,
			comment: formData?.financeApproval?.comment,
		},
		{
			admin: "1st Line Admin (Finance Dept)",
			status: formData?.approvalStatus,
			comment: "N/A",
		},
	];

	useEffect(() => {
		if (toggleFundLeaseRequestModal) {
			document.body.style.overflow = "hidden";
		}
		return () => {
			// eslint-disable-next-line no-undef
			document.body.style.overflow = "auto";
		};
	}, [toggleFundLeaseRequestModal]);

	const requestStatus = leaseData?.usersRequest?.find((a) => a?.transactionId === leaseData?.objectId);

	const leaseStatus = getApprovalStatus(leaseData, requestStatus);

	const fetchSubmittedQuickLease = async () => {
		const data = await getSubmittedQuickLease();
		dispatch(saveAdminQuickRequests(data));
	};

	useEffect(() => {
		fetchSubmittedQuickLease();
	}, []);

	return (
		<>
			<Backdrop open={toggleFundLeaseRequestModal} sx={{ zIndex: 13 }}>
				<FundLeaseRequest
					fundRequestTab={fundRequestTab}
					setFundRequestTab={setFundRequestTab}
					leaseData={leaseData}
				/>
			</Backdrop>
			<StyledForm onSubmit={handleSubmit}>
				<div className='RightSection w-full'>
					<FormAccordion title='Request Information' open={true}>
						<div className='grid grid-cols-3 gap-3'>
							<div className='form-group'>
								<label>DATE / TIME</label>
								<InputField
									name='createdAt'
									handleChange={handleInputChange}
									isEditable={isEditable}
									value={getFormattedDate(formData?.createdAt)}
								/>
							</div>

							<div className='form-group'>
								<label>REQUEST</label>
								<InputField
									name='productName'
									handleChange={handleInputChange}
									isEditable={isEditable}
									value={formData?.productName}
								/>
							</div>

							<div className='form-group'>
								<label>LEASE TYPE</label>
								<SelectField
									name='leaseType'
									handleChange={handleInputChange}
									isEditable={isEditable}
									value={formData?.leaseType}
								/>
							</div>

							<div className='form-group'>
								<label>LEASEE</label>
								<InputField
									name='leasee'
									handleChange={handleInputChange}
									isEditable={isEditable}
									value={getUserName(formData)}
								/>
							</div>

							<div className='form-group'>
								<label>CATEGORY</label>
								<InputField
									name='productCategory'
									handleChange={handleInputChange}
									isEditable={isEditable}
									value={formData?.productCategory}
								/>
							</div>

							<div className='form-group'>
								<label>BRAND</label>
								<InputField
									name='brand'
									handleChange={handleInputChange}
									isEditable={isEditable}
									value={formData?.brand}
								/>
							</div>

							<div className='form-group'>
								<label>MODEL</label>
								<InputField
									name='model'
									handleChange={handleInputChange}
									isEditable={isEditable}
									value={formData?.model}
								/>
							</div>

							<div className='form-group'>
								<label>AMOUNT</label>
								<InputField
									name='amount'
									handleChange={handleInputChange}
									isEditable={isEditable}
									value={getFormattedAmount.format(formData?.amount)}
								/>
							</div>

							<div className='form-group'>
								<label>YEAR</label>
								<InputField
									name='year'
									handleChange={handleInputChange}
									isEditable={isEditable}
									value={formData?.year}
								/>
							</div>

							<div className='form-group'>
								<label>CONDITION</label>
								<InputField
									name='condition'
									handleChange={handleInputChange}
									isEditable={isEditable}
									value={formData?.condition}
								/>
							</div>

							<div className='form-group'>
								<label>COLOR</label>
								<InputField
									name='color'
									handleChange={handleInputChange}
									isEditable={isEditable}
									value={formData?.color}
								/>
							</div>

							<div className='form-group'>
								<label>DELIVERY FEE</label>
								<InputField
									name='deliveryFee'
									handleChange={handleInputChange}
									isEditable={isEditable}
									value={formData?.deliveryFee}
								/>
							</div>

							<div className='form-group'>
								<label>TRN ID</label>
								<InputField name='trnId' value={"TRN-" + formData?.objectId} />
							</div>

							<div className='form-group'>
								<label>APPROVAL</label>
								<SelectFieldStatus
									name='approvalStatus'
									type={leaseStatus?.toLowerCase()}
									// type={formData?.approvalStatus?.toLowerCase()}
									value={leaseStatus}
									// value={formData?.approvalStatus}
								/>
							</div>

							<div className='form-group'>
								<label>REQUEST TYPE</label>
								<InputField
									name='request_type'
									handleChange={handleInputChange}
									isEditable={isEditable}
									value={getRequestValue(formData)}
								/>
							</div>

							<div className='form-group'>
								<label>LEASOR</label>
								<InputField
									name='leasor'
									handleChange={handleInputChange}
									isEditable={isEditable}
									value={getLeasor(formData)}
								/>
							</div>
						</div>
					</FormAccordion>

					<FormAccordion title='Product description'>
						<div className='form-group'>
							<TextAreaField
								name='product_desc'
								value={formData?.description || formData?.productId.description}
							/>
						</div>
					</FormAccordion>

					<FormAccordion title='Key features'>
						<div className='form-group'>
							<TextAreaField
								name='product_features'
								value={formData?.keyFeatures && formData?.keyFeatures?.replace(/;/g, "<br/>")}
							/>
						</div>
					</FormAccordion>

					<FormAccordion title='Product specifications'>
						<div className='form-group'>
							<TextAreaField
								name='product_specs'
								value={formData?.specifications && formData?.specifications?.replace(/;/g, "<br/>")}
							/>
						</div>
					</FormAccordion>

					<FormAccordion title='Leasee’s Information'>
						<LeaseeInformation formData={formData} />
					</FormAccordion>

					<FormAccordion title='ID card'>
						<div className='form-group'>
							<FileGroup name='id_card' value={formData?.userId?.idCard} />
						</div>
					</FormAccordion>

					<FormAccordion title='Utility bill'>
						<div className='form-group'>
							<FileGroup name='utility_bill' value={formData?.userId?.utilityBill} />
						</div>
					</FormAccordion>

					<FormAccordion title='Auto debit'>
						<div className='grid grid-cols-3 gap-3'>
							<div className='form-group'>
								<SelectFieldStatus
									name='auto_debit'
									handleChange={handleInputChange}
									isEditable={isEditable}
									value={formData?.auto_debit}
									type={"success"}
								/>
							</div>
						</div>
					</FormAccordion>

					<FormAccordion title='Insurance'>
						<div className='grid grid-cols-3 gap-3'>
							<div className='form-group'>
								<SelectFieldStatus
									name='insurance'
									handleChange={handleInputChange}
									isEditable={isEditable}
									value={formData?.insurance}
									type={"success"}
								/>
							</div>
						</div>
					</FormAccordion>

					<FormAccordion title='Guarantor 1'>
						<Guarantor_1 formData={formData} />
					</FormAccordion>

					<FormAccordion title='Guarantor 2'>
						<Guarantor_2 formData={formData} />
					</FormAccordion>

					<FormAccordion title='Credit assessment'>
						<div className='grid grid-cols-3 gap-3'>
							<div className='form-group'>
								<SelectFieldStatus
									name='credit_assessment'
									handleChange={handleInputChange}
									isEditable={isEditable}
									value={formData?.credit_assessment}
									type={"success"}
								/>
							</div>
						</div>
					</FormAccordion>

					{/* {formData?.profile === "Investor" && (
						<FormAccordion title="Negotiators" open={true}></FormAccordion>
					)} */}
					{formData?.leaseType?.toLowerCase().includes("quick") && (
						<FormAccordion title='Approval Status' open={false}>
							<AdminApprovalStatus adminLevels={adminComments} />
						</FormAccordion>
					)}
					{formData?.leaseType?.toLowerCase().includes("quick") && (
						<FormAccordion title='Request Status' open={true}>
							<RequestStatusForm
								formData={formData}
								handleInputChange={handleInputChange}
								service='lease'
								adminComments={adminComments}
								onBeginFundRequest={onBeginFundRequest}
							/>
						</FormAccordion>
					)}
					{formData?.leaseType?.toLowerCase().includes("sponsored") && (
						<FormAccordion title='Request Status' open={false}>
							<SponsoredRequestStatus leaseData={leaseData} />
						</FormAccordion>
					)}
				</div>
			</StyledForm>
		</>
	);
};

// Props Validation
RequestLeaseForm.propTypes = {
	leaseData: PropTypes.object,
	setLeaseData: PropTypes.func,
	updateLeaseObject: PropTypes.func,
};

export default RequestLeaseForm;
